/**
 * Zapis formule v graditelju kazalnikov (docs/ARHITEKTURA.md, 6.3 – Graditelj).
 *
 * Skrbnik formulo vpiše kot v Excelu (`=A7/A1*100`, `SUM(A1;A3;A8)`) ali jo
 * sestavi s klikanjem po celicah. Besedilo se tu razčleni v drevo in nato s
 * koordinatami seje pretvori v drevo izraza (`Izraz`), ki gre na strežnik:
 * vsaka celica postane sklic – vir vrstice, njena različica (agregacija) in
 * časovni kontekst stolpca. Koordinate (A1, B7) se ne shranijo; ob
 * naslednjem odprtju jih vmesnik dodeli znova.
 *
 * Besedilo se nikoli ne izvaja kot koda. Dovoljeno je samo: števila, celice
 * A/B/C, `+ − * /`, oklepaji, predznak in `SUM` z naštetimi celicami ali
 * izrazi, ločenimi s podpičjem. Razpon (`A1:A5`) je zavrnjen, ker bi ob novi
 * vrstici v shemi tiho spremenil pomen formule.
 */
import type { Izraz, ObdobjeSklica, SklicFormule } from '@koda/shared';

/** Stolpec mreže: A tekoče obdobje, B isto obdobje primerjalnega leta, C konec primerjalnega leta. */
export type Stolpec = 'A' | 'B' | 'C';

export const OBDOBJE_STOLPCA: Record<Stolpec, ObdobjeSklica> = {
  A: 'current',
  B: 'prior_same_period',
  C: 'prior_year_end',
};

export const STOLPEC_OBDOBJA: Record<ObdobjeSklica, Stolpec> = {
  current: 'A',
  prior_same_period: 'B',
  prior_year_end: 'C',
};

/** Razčlenjena formula: celice so še koordinate te seje. */
export type DrevoFormule =
  | { tip: 'stevilo'; vrednost: string }
  | { tip: 'celica'; stolpec: Stolpec; vrstica: number }
  | { tip: 'predznak'; arg: DrevoFormule }
  | { tip: 'op'; op: 'add' | 'sub' | 'mul' | 'div'; left: DrevoFormule; right: DrevoFormule }
  | { tip: 'vsota'; args: DrevoFormule[] };

/** Vrste napak; besedilo v jeziku uporabnika sestavi vmesnik. */
export type VrstaNapake =
  | 'konec' // formula se konča prezgodaj
  | 'pricakovan' // manjka znak, npr. zaklepaj
  | 'razpon' // A1:A5 v SUM
  | 'stolpec' // stolpec, ki ga ni (D5)
  | 'znak' // nerazumljiv znak
  | 'odvec' // besedilo za koncem formule
  | 'stevilo' // dvoumno število (1.000)
  | 'celica'; // celica s to številko ne obstaja ali ni izbirna

export class NapakaFormule extends Error {
  constructor(
    readonly vrsta: VrstaNapake,
    /** Mesto v besedilu (od 1) ali koordinata celice. */
    readonly mesto: string,
    /** Kaj je bilo pričakovano ali najdeno. */
    readonly podrobnost = '',
  ) {
    super(`${vrsta} ${mesto} ${podrobnost}`.trim());
  }
}

/**
 * Razčleni besedilo formule. Prazno besedilo (ali samo `=`) je `null`.
 * Prednost: predznak, nato `* /`, nato `+ −`; od leve proti desni.
 */
export function razcleniFormulo(besedilo: string): DrevoFormule | null {
  const src = besedilo.trim().replace(/^=/, '');
  let i = 0;
  const mesto = (): string => String(i + 1);
  const preskoci = (): void => {
    while (i < src.length && /\s/.test(src[i]!)) i += 1;
  };
  const je = (c: string): boolean => {
    preskoci();
    return src[i] === c;
  };
  const vzemi = (c: string): void => {
    if (!je(c)) throw i >= src.length ? new NapakaFormule('konec', mesto(), c) : new NapakaFormule('pricakovan', mesto(), c);
    i += 1;
  };

  const primarno = (): DrevoFormule => {
    preskoci();
    if (i >= src.length) throw new NapakaFormule('konec', mesto());
    const c = src[i]!;
    if (c === '(') {
      i += 1;
      const e = izraz();
      vzemi(')');
      return e;
    }
    // Predznak: tudi znak U+2212 (»−«), ki ga vpiše slovenska tipkovnica ali lepljenje.
    if (c === '-' || c === '−') {
      i += 1;
      return { tip: 'predznak', arg: primarno() };
    }
    if (c === '+') {
      i += 1;
      return primarno();
    }
    const ostalo = src.slice(i);
    if (/^sum\s*\(/i.test(ostalo)) {
      i += 3;
      vzemi('(');
      const args = [izraz()];
      for (;;) {
        if (je(':')) throw new NapakaFormule('razpon', mesto());
        if (!je(';')) break;
        i += 1;
        args.push(izraz());
      }
      vzemi(')');
      return { tip: 'vsota', args };
    }
    const celica = /^([A-Ca-c])(\d{1,4})(?![\d.,])/.exec(ostalo);
    if (celica) {
      i += celica[0].length;
      if (je(':')) throw new NapakaFormule('razpon', mesto());
      return { tip: 'celica', stolpec: celica[1]!.toUpperCase() as Stolpec, vrstica: Number(celica[2]) };
    }
    const stevilo = /^\d+([.,]\d+)?/.exec(ostalo);
    if (stevilo) {
      // »1.000« je v slovenščini tisoč, ne ena: dvoumno, zato zavrnjeno.
      if (/^\d{1,3}\.\d{3}$/.test(stevilo[0])) throw new NapakaFormule('stevilo', mesto(), stevilo[0]);
      i += stevilo[0].length;
      return { tip: 'stevilo', vrednost: stevilo[0].replace(',', '.') };
    }
    if (c === ':') throw new NapakaFormule('razpon', mesto());
    if (/^[D-Zd-z]\d/.test(ostalo)) throw new NapakaFormule('stolpec', mesto(), c.toUpperCase());
    throw new NapakaFormule('znak', mesto(), c);
  };

  const clen = (): DrevoFormule => {
    let a = primarno();
    for (;;) {
      if (je('*') || je('×')) {
        i += 1;
        a = { tip: 'op', op: 'mul', left: a, right: primarno() };
      } else if (je('/')) {
        i += 1;
        a = { tip: 'op', op: 'div', left: a, right: primarno() };
      } else return a;
    }
  };

  const izraz = (): DrevoFormule => {
    let a = clen();
    for (;;) {
      if (je('+')) {
        i += 1;
        a = { tip: 'op', op: 'add', left: a, right: clen() };
      } else if (je('-') || je('−')) {
        i += 1;
        a = { tip: 'op', op: 'sub', left: a, right: clen() };
      } else return a;
    }
  };

  if (src.trim() === '') return null;
  const drevo = izraz();
  preskoci();
  if (i < src.length) throw new NapakaFormule('odvec', mesto(), src.slice(i));
  return drevo;
}

/** Celice formule (vsaka pojavitev), za označevanje uporabljenih celic v mreži. */
export function celiceFormule(drevo: DrevoFormule | null): { stolpec: Stolpec; vrstica: number }[] {
  if (drevo === null) return [];
  switch (drevo.tip) {
    case 'celica':
      return [{ stolpec: drevo.stolpec, vrstica: drevo.vrstica }];
    case 'predznak':
      return celiceFormule(drevo.arg);
    case 'op':
      return [...celiceFormule(drevo.left), ...celiceFormule(drevo.right)];
    case 'vsota':
      return drevo.args.flatMap(celiceFormule);
    default:
      return [];
  }
}

/**
 * Drevo formule v drevo izraza za strežnik. `vir` za številko vrstice vrne
 * vir in agregacijo različice ali `null`, če celice ni (ali ni izbirna).
 */
export function vIzraz(
  drevo: DrevoFormule,
  vir: (vrstica: number) => Omit<SklicFormule, 'period'> | null,
): Izraz {
  switch (drevo.tip) {
    case 'stevilo':
      return { op: 'num', value: drevo.vrednost };
    case 'celica': {
      const s = vir(drevo.vrstica);
      if (s === null) throw new NapakaFormule('celica', `${drevo.stolpec}${String(drevo.vrstica)}`);
      return { op: 'ref', source: s.source, aggregation: s.aggregation, period: OBDOBJE_STOLPCA[drevo.stolpec] };
    }
    case 'predznak':
      return { op: 'neg', arg: vIzraz(drevo.arg, vir) };
    case 'op':
      return { op: drevo.op, left: vIzraz(drevo.left, vir), right: vIzraz(drevo.right, vir) };
    case 'vsota':
      return { op: 'sum', args: drevo.args.map((a) => vIzraz(a, vir)) };
  }
}

const PREDNOST: Record<'add' | 'sub' | 'mul' | 'div', number> = { add: 1, sub: 1, mul: 2, div: 2 };
const ZNAK: Record<'add' | 'sub' | 'mul' | 'div', string> = { add: '+', sub: '-', mul: '*', div: '/' };

/**
 * Drevo izraza nazaj v besedilo s koordinatami te seje (za urejanje shranjene
 * formule). `koordinata` za sklic vrne številko vrstice ali `null`, če vira
 * v mreži ni – takrat je rezultat `null`. Oklepaji samo, kjer so potrebni.
 */
export function vBesedilo(izraz: Izraz, koordinata: (s: Omit<SklicFormule, 'period'>) => number | null): string | null {
  const rek = (e: Izraz): string => {
    switch (e.op) {
      case 'num':
        return e.value.replace('.', ',');
      case 'ref': {
        const st = koordinata({ source: e.source, aggregation: e.aggregation });
        if (st === null) throw new NapakaFormule('celica', '?');
        return `${STOLPEC_OBDOBJA[e.period]}${String(st)}`;
      }
      case 'neg': {
        const a = rek(e.arg);
        return e.arg.op === 'add' || e.arg.op === 'sub' ? `-(${a})` : `-${a}`;
      }
      case 'sum':
        return `SUM(${e.args.map(rek).join(';')})`;
      default: {
        const p = PREDNOST[e.op];
        const levo = rek(e.left);
        const desno = rek(e.right);
        const okl = (del: Izraz, s: string, desni: boolean): string => {
          if (del.op === 'add' || del.op === 'sub' || del.op === 'mul' || del.op === 'div') {
            const q = PREDNOST[del.op];
            // Na desni strani odštevanja ali deljenja tudi enaka prednost potrebuje oklepaj.
            if (q < p || (desni && q === p && (e.op === 'sub' || e.op === 'div'))) return `(${s})`;
          }
          return s;
        };
        return `${okl(e.left, levo, false)}${ZNAK[e.op]}${okl(e.right, desno, true)}`;
      }
    }
  };
  try {
    return `=${rek(izraz)}`;
  } catch (e) {
    if (e instanceof NapakaFormule) return null;
    throw e;
  }
}
